import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  Heading,
  Input,
  Modal,
  ModalOverlay,
} from 'react-aria-components';
import { Search, type LucideIcon } from 'lucide-react';
import styles from './CommandPalette.module.css';

// Group order is the result order: records first, then destinations (DESIGN_SYSTEM.md §10.13).
const GROUPS = [
  'Findings',
  'Engagements',
  'Evidence',
  'Workspace',
  'Settings',
  'Support',
  'Switch workspace',
] as const;
export type PaletteGroup = (typeof GROUPS)[number];
const PAGE_GROUPS: readonly PaletteGroup[] = [
  'Workspace',
  'Settings',
  'Support',
  'Switch workspace',
];
const MAX_RESULTS = 50;

export interface CommandPaletteItem {
  id: string;
  label: string;
  description: string;
  group: PaletteGroup;
  icon: LucideIcon;
  keywords?: readonly string[];
  /** Opens the item; a search selection never approves, releases or starts work. */
  run: () => Promise<void>;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly CommandPaletteItem[];
  onSelect: (item: CommandPaletteItem) => void;
}

function searchableText(value: string) {
  return value.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
}

export function CommandPalette({ isOpen, onOpenChange, items, onSelect }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeOptionRef = useRef<HTMLButtonElement>(null);
  const normalizedQuery = searchableText(query.trim());
  const results = useMemo(
    () =>
      items
        .filter((item) => {
          // Without a query the palette is a page switcher; records appear once typed for.
          if (!normalizedQuery) return PAGE_GROUPS.includes(item.group);
          const text = searchableText(
            [item.label, item.description, ...(item.keywords ?? [])].join(' '),
          );
          return normalizedQuery.split(/\s+/).every((term) => text.includes(term));
        })
        .slice(0, MAX_RESULTS),
    [items, normalizedQuery],
  );
  const activeItem = results[activeIndex];
  const groups = GROUPS.map((group) => ({
    label: group,
    items: results.filter((item) => item.group === group),
  })).filter((group) => group.items.length > 0);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, results]);

  const changeOpen = (open: boolean) => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
    }
    onOpenChange(open);
  };

  const select = (item: CommandPaletteItem) => {
    changeOpen(false);
    onSelect(item);
  };

  return (
    <ModalOverlay
      className={styles.overlay}
      isOpen={isOpen}
      onOpenChange={changeOpen}
      isDismissable
    >
      <Modal className={styles.modal}>
        <AriaDialog aria-label="Command palette" className={styles.dialog}>
          <Heading slot="title" className={styles.visuallyHidden}>
            Search Traceira
          </Heading>
          <div className={styles.searchRow}>
            <Search size={20} className={styles.searchIcon} aria-hidden="true" />
            <Input
              ref={inputRef}
              className={styles.input}
              aria-label="Search workspace"
              placeholder="Search findings, evidence, engagements or pages…"
              aria-describedby="command-palette-help"
              aria-controls="command-palette-results"
              aria-expanded="true"
              aria-autocomplete="list"
              autoComplete="off"
              aria-activedescendant={activeItem ? `command-result-${activeItem.id}` : undefined}
              role="combobox"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  if (results.length === 0) return;
                  event.preventDefault();
                  const direction = event.key === 'ArrowDown' ? 1 : -1;
                  setActiveIndex((index) => (index + direction + results.length) % results.length);
                } else if (event.key === 'Enter') {
                  if (activeItem) {
                    event.preventDefault();
                    select(activeItem);
                  }
                }
              }}
            />
            <AriaButton
              className={styles.close}
              aria-label="Close search"
              onPress={() => changeOpen(false)}
            >
              Esc
            </AriaButton>
          </div>

          <p id="command-palette-help" className={styles.help}>
            Search records by title, asset or ID, or jump to a page. Use the arrow keys to move and
            Enter to open.
          </p>

          {results.length > 0 ? (
            <div
              id="command-palette-results"
              className={styles.results}
              role="listbox"
              aria-label="Results"
            >
              {groups.map((group) => (
                <div key={group.label} role="group" aria-label={group.label}>
                  <span className={styles.groupHeading} aria-hidden="true">
                    {group.label}
                  </span>
                  {group.items.map((item) => {
                    const index = results.indexOf(item);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        id={`command-result-${item.id}`}
                        type="button"
                        ref={index === activeIndex ? activeOptionRef : undefined}
                        role="option"
                        aria-selected={index === activeIndex}
                        tabIndex={-1}
                        className={styles.option}
                        data-active={index === activeIndex ? 'true' : undefined}
                        onMouseMove={() => setActiveIndex(index)}
                        onClick={() => select(item)}
                      >
                        <Icon size={18} aria-hidden="true" />
                        <span className={styles.optionCopy}>
                          <span className={styles.optionLabel}>{item.label}</span>
                          <span className={styles.optionDescription}>{item.description}</span>
                        </span>
                        {index === activeIndex && (
                          <span className={styles.enterHint} aria-hidden="true">
                            Enter ↵
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty} role="status">
              <p className={styles.emptyTitle}>No matches</p>
              <p>Try a finding title, an asset, a record ID or a page name such as Findings.</p>
              <AriaButton
                className={styles.clearSearch}
                onPress={() => {
                  setQuery('');
                  setActiveIndex(0);
                  inputRef.current?.focus();
                }}
              >
                Clear search
              </AriaButton>
            </div>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}
